/**
 * Structured data (JSON-LD) for every page, built as plain objects.
 *
 * The site used to type its JSON-LD as template strings. A trailing comma and
 * a raw line break in them left 354 of 355 pages with a block JSON.parse
 * rejects, so Google ignored their schema ("Unparsable structured data" in
 * Search Console). The strings had also lowercased schema.org names
 * ("service", "offercatalog"), which are case-sensitive.
 *
 * Build nodes with the functions below and pass them to
 * <Layout schema={[...]} />, which renders one @graph per page through
 * JSON.stringify. Never write JSON-LD as a string: the build fails if a page
 * ships a block that does not parse (jsonld-check.js, run from gatsby-node.js).
 */
import siteConfig from "../siteConfig";
import { siteHref } from "./siteUrls";

export const ORIGIN = "https://roachhomeimprovement.com";

// The company's one entity, the Battle Creek headquarters. Every page carries
// this node under this @id, so its content must not vary by page.
export const BUSINESS_ID = `${ORIGIN}/#HomeAndConstructionBusiness`;

// gatsby-plugin-manifest writes this file from src/images/icon.png (the 512px
// red Roach logo) on every build. The logo the markup used before sat on a
// Contentful space that no longer exists and answered 403.
export const LOGO_URL = `${ORIGIN}/icons/icon-512x512.png`;

// The hours the markup has always carried. The site itself shows no hours, so
// check any change against the Google Business Profile first.
const OPENING_HOURS = [
  {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    opens: "08:00",
    closes: "18:00",
  },
];

const HEADQUARTERS = siteConfig.offices.find((office) => office.headquarters);

// Towns whose own Wikipedia article is "<Town>, Michigan", checked with the
// Wikipedia API on 2026-11-05. Ada, Bailey, Bradley, Conklin, Coral, Dorr,
// Gowen, Hamilton, Moline and Shelbyville redirect to a township article (a
// different place) and Texas Corners has no article, so those get no sameAs.
// Add a new town only after checking its article.
const TOWNS_WITH_WIKIPEDIA_ARTICLE = new Set([
  "allegan", "allendale", "alto", "athens", "battle creek", "bellevue", "byron center",
  "caledonia", "cannonsburg", "cedar springs", "charlotte", "comstock park", "coopersville",
  "delton", "douglas", "east leroy", "fennville", "ferrysburg", "freeport", "fruitport",
  "galesburg", "grand haven", "grand rapids", "grandville", "grant", "hastings",
  "hickory corners", "holland", "hopkins", "howard city", "jackson", "kalamazoo", "kent city",
  "lake odessa", "lowell", "marshall", "middleville", "muskegon", "pierson", "plainwell",
  "portage", "richland", "rockford", "sand lake", "sparta", "trufant", "vicksburg", "wayland",
  "wyoming",
]);

// What each service page's offer catalog lists: the services that page's own
// copy names (Contentful, checked against the built pages on 2026-11-05).
// Update a list when that copy is rewritten, so the markup never names a
// service the page does not mention.
export const SERVICE_PAGE_CATALOGS = {
  roofing: {
    name: "Roofing Services",
    services: ["Roofing Repair", "Roof Replacement", "Metal Roofing", "Roofing Installation"],
  },
  siding: {
    name: "Siding Services",
    services: ["Siding Installation", "Siding Repair", "Siding Replacement"],
  },
  "windows-doors": {
    name: "Windows and Doors Services",
    services: ["Window Replacement", "Door Installation", "Custom Windows and Doors"],
  },
};

// The same for town pages, keyed by service slug. All 60 towns share one
// Contentful entry per service. Roofing town pages are not listed: their
// catalog is the service cards they render (RoofingServicesGrid).
export const TOWN_PAGE_CATALOGS = {
  "siding-contractor": {
    name: "Siding Services",
    services: ["Vinyl Siding", "Fiber Cement Siding", "Wood Siding"],
  },
  "window-replacement-contractor": {
    name: "Window Replacement Services",
    services: [
      "Double-Hung Window Replacement",
      "Casement Window Replacement",
      "Sliding Window Replacement",
      "Bay and Bow Window Replacement",
      "Picture Window Replacement",
    ],
  },
  "exterior-door-installers": {
    name: "Exterior Door Services",
    services: ["Door Installation", "Exterior Door Replacement"],
  },
};

// Leaves out keys with no value, so a missing fact is omitted rather than sent
// as "" or [] (a town without a ZIP used to ship "postalcode": "").
const compact = (node) => {
  const out = {};
  Object.keys(node).forEach((key) => {
    const value = node[key];
    const empty =
      value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);
    if (!empty) out[key] = value;
  });
  return out;
};

/** Absolute, trailing-slash URL of a page, as in its canonical tag. */
export const pageUrl = (pathname) => `${ORIGIN}${siteHref(pathname || "/")}`;

const officeId = (office) =>
  office === HEADQUARTERS ? BUSINESS_ID : `${ORIGIN}/#office-${office.city.toLowerCase().replace(/\s+/g, "-")}`;

const businessFields = (office) => {
  const telephone = siteConfig.phoneE164(office.phone);
  return {
    "@type": "HomeAndConstructionBusiness",
    "@id": officeId(office),
    name: siteConfig.companyName,
    url: `${ORIGIN}/`,
    logo: LOGO_URL,
    image: LOGO_URL,
    description:
      `Roach Home Improvement is a locally owned and operated full service remodeling contractor in ${office.city}, ` +
      `${office.state} specializing in roofing repair, replacement, and installation for residential and commercial projects.`,
    telephone,
    priceRange: "$$",
    address: {
      "@type": "PostalAddress",
      streetAddress: office.address,
      addressLocality: office.city,
      addressRegion: office.state,
      postalCode: office.zip,
      addressCountry: "US",
    },
    openingHoursSpecification: OPENING_HOURS,
    contactPoint: { "@type": "ContactPoint", contactType: "sales", telephone },
  };
};

/** The headquarters node. Layout puts it on every page. */
export const businessNode = ({ email, sameAs } = {}) => compact({ ...businessFields(HEADQUARTERS), email, sameAs });

/**
 * The node for the branch office that serves a town (Jackson, Wyoming), or null.
 * A town without an office gets no business node of its own, only areaServed
 * on the page's Service node; Battle Creek is the headquarters node itself.
 */
export const officeNodeForCity = (city) => {
  const office = siteConfig.officeForCity(city);
  if (!office || office === HEADQUARTERS) return null;
  return { ...businessFields(office), parentOrganization: { "@id": BUSINESS_ID } };
};

/** @id of the business that serves a town: its branch office, or the headquarters. */
export const providerIdForCity = (city) => {
  const office = siteConfig.officeForCity(city);
  return office ? officeId(office) : BUSINESS_ID;
};

/** The homepage's WebSite node; Google reads the site name from it. */
export const websiteNode = () => ({
  "@type": "WebSite",
  "@id": `${ORIGIN}/#website`,
  url: `${ORIGIN}/`,
  name: siteConfig.companyName,
  publisher: { "@id": BUSINESS_ID },
});

/** A served town. Contentful town names carry stray spaces, so it is trimmed here. */
export const cityPlace = (city) => {
  const name = String(city || "").trim();
  return compact({
    "@type": "City",
    name,
    containedInPlace: { "@type": "State", name: "Michigan" },
    sameAs: TOWNS_WITH_WIKIPEDIA_ARTICLE.has(name.toLowerCase())
      ? `https://en.wikipedia.org/wiki/${name.replace(/ /g, "_")},_Michigan`
      : undefined,
  });
};

/** Every town in a list once, for areaServed. */
export const cityPlaces = (cities) =>
  Array.from(new Set(cities.map((city) => String(city || "").trim()).filter(Boolean))).map(cityPlace);

/**
 * The Service a page offers. `services` are the names of the services the page
 * itself lists; they become its OfferCatalog.
 */
export const serviceNode = ({ pathname, serviceType, providerId, areaServed, catalogName, services }) => {
  const url = pageUrl(pathname);
  return compact({
    "@type": "Service",
    "@id": `${url}#service`,
    serviceType: String(serviceType || "").trim(),
    url,
    provider: { "@id": providerId },
    areaServed,
    hasOfferCatalog:
      services && services.length
        ? {
            "@type": "OfferCatalog",
            name: catalogName,
            itemListElement: services.map((name) => ({
              "@type": "Offer",
              itemOffered: { "@type": "Service", name },
            })),
          }
        : undefined,
  });
};

/** URLs of the social profiles that are filled in (an empty one hides its footer icon). */
export const profileUrls = (social) =>
  Object.keys(social || {})
    .map((key) => social[key])
    .filter((url) => url && url !== "#");

/** A page's one JSON-LD document. */
export const jsonLdGraph = (nodes) => ({
  "@context": "https://schema.org",
  "@graph": nodes.filter(Boolean),
});

// "<" is escaped so no text in the data can close the <script> element early.
export const serializeJsonLd = (data) => JSON.stringify(data).replace(/</g, "\\u003c");
